(() => {
  const updateCount = (root, count) => {
    if (!Number.isInteger(count) || count < 0) return;
    root.querySelectorAll('[data-search-model-count]').forEach(node => {
      const variant = count === 0 ? 'searchCountZero' : count === 1 ? 'searchCountOne' : 'searchCountOther';
      node.textContent = node.dataset[variant].replace('__COUNT__', String(count))
        .replace('__SEARCH_TERMS__', node.dataset.searchModelTerms || '');
      // The full result list already owns the zero-result message.
      // Keep the header counter available again after filters are cleared.
      const description = node.closest('.PageHeader--collection .SectionHeader__Description');
      if (description && root.hasAttribute('data-search-models')) description.hidden = count === 0;
    });
  };

  // The predictive list uses a compact index for an exact model count across
  // all Shopify pages. Each response renders only the configured preview limit.
  const fetchModels = async (input, options = {}) => {
    const request = new URL(input, location.href);
    if (request.origin !== location.origin) throw new Error('Invalid search origin');
    request.searchParams.delete('page');
    const expectedTheme = String(window.Shopify?.theme?.id || '');
    if (!expectedTheme) throw new Error('Missing search theme');
    // The current session has already selected the rendered theme.
    // Preview redirects can rewrite repeated facets and nested search options.
    request.searchParams.delete('preview_theme_id');
    const load = async url => {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error('Search unavailable');
      const content = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = content.querySelector('[data-search-model-source]');
      if (!source) throw new Error('Missing search results');
      if (String(source.dataset.renderedThemeId) !== expectedTheme) throw new Error('Search theme mismatch');
      return { content, source };
    };
    const first = await load(request);
    const total = Number(first.source.dataset.searchTotal);
    const pages = Number(first.source.dataset.searchPages);
    const limit = Number(first.source.dataset.searchLimit);
    if (!Number.isInteger(total) || total < 0 || total > 1000 ||
      !Number.isInteger(pages) || pages < 0 || pages > 20 || !Number.isInteger(limit) || limit < 1) {
      throw new Error('Invalid search pagination');
    }
    const fragments = [first];
    let next = 2;
    await Promise.all(Array.from({ length: Math.min(3, Math.max(0, pages - 1)) }, async () => {
      while (next <= pages) {
        const url = new URL(request);
        url.searchParams.set('page', next++);
        fragments[Number(url.searchParams.get('page')) - 1] = await load(url);
      }
    }));
    const records = fragments.reduce((count, fragment) => count + Number(fragment.source.dataset.searchRecordCount), 0);
    if (records !== total) throw new Error('Incomplete search results');
    const models = new Set();
    const ids = new Set();
    const cells = new Map();
    fragments.forEach(({ source }) => {
      source.querySelector('template[data-search-model-index]')?.content.querySelectorAll('[data-search-model-key]').forEach(node => {
        if (ids.has(node.dataset.searchRecordId)) return;
        ids.add(node.dataset.searchRecordId);
        models.add(node.dataset.searchModelKey);
      });
      source.querySelectorAll('.ProductList > [data-search-model-key]').forEach(cell => {
        if (!cells.has(cell.dataset.searchModelKey)) cells.set(cell.dataset.searchModelKey, cell);
      });
    });
    const grid = first.source.querySelector('.ProductList');
    if (models.size && (!grid || cells.size < Math.min(limit, models.size))) throw new Error('Missing search cards');
    if (grid) grid.replaceChildren(...[...cells.values()].slice(0, limit));
    updateCount(first.source, models.size);
    first.source.dataset.searchModelsReady = 'true';
    first.source.dataset.searchModelCount = models.size;
    return new Response(first.content.body.innerHTML, { headers: { 'Content-Type': 'text/html' } });
  };

  window.ProductSearchModels = { fetch: fetchModels, updateCount };
})();
